// libraries.
import { useSelector, useDispatch } from 'react-redux';
import { electricPurple600, scarpaGray50 } from '@habitech/shared';
import { Col, Row } from 'react-bootstrap';
import { useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';

// slices.
import {
  getIsDrawingPolygon,
  getPolygonTypeFilter,
} from '@domains/polygon-tool/store/slices/polygon-slice';

// components.
import GoogleMap from '@domains/google-map/GoogleMap';
import { EditorMode } from './components/EditorMode';
import { DrawingPolygon } from '@domains/polygon-tool/components/DrawingPolygon';
import { Layout } from '@shared';
import { PolygonActionsBar } from './components/PolygonActionsBar';
import { PolygonsInMedianZones } from '@domains/median-zones';
import { SidebarPolygons } from './components/SideBarPolygons/SideBarPolygons';
import { CenterMedianZone } from './components/CenterMedianZone/CenterMedianZone';
import { LayoutPolygonTool } from './components/LayoutPolygonTool/LayoutPolygonTool';
import {
  ListPolygons,
  updateIntersectionHandlerListDirectly,
  updateIntersectionListHandlerFromServerData,
} from '@domains/save-polygons';
import { PolygonsIntersection } from '@domains/save-polygons/components/CardsInterceptionPolygon/PolygonsInterception';
import { AuditSelectedPolygon } from '@domains/audit-polygons/components/AuditSelectedPolygon/AuditSelectedPolygon';

// selectors.
import { getCountry } from '@domains/user/store/slices/user-slice/user-slice';
import {
  getMedianZoneCode,
  getShowPolygons,
} from '@domains/median-zones/store/slices/medianZones.slice';

// hooks.
import { useGetSavedIntersectionsQuery } from '@domains/save-polygons/store/api-slices/savedIntersections.endpoints';
import { useGetPolygonsByMedianZoneQuery } from '@domains/save-polygons/store/api-slices/getPolygons.endpoints';
import { setListPolygons } from '@domains/save-polygons/store/slices/modePolygon.slice';
import { getRadiusByCountry } from '@domains/save-polygons/store/api-slices/utils';

export const PolygonsDashboard = () => {
  const dispatch = useDispatch();
  const isDrawing = useSelector(getIsDrawingPolygon);

  const borderColor = isDrawing ? electricPurple600 : scarpaGray50;

  const country = useSelector(getCountry);
  const medianZoneId = useSelector(getMedianZoneCode);
  const showPolygons = useSelector(getShowPolygons);
  const polygonTypeFilter = useSelector(getPolygonTypeFilter);

  const { data } = useGetSavedIntersectionsQuery({
    country,
    median_zone_id: medianZoneId,
  });

  // filter='all' = unión exacta de "Solo estándar" + "Solo bloqueos". Cada
  // sub-vista usa su propio radio (1300/1200 para standard, 5000 para
  // blocking) para que la unión de Todos sea idéntica a la suma de las dos
  // vistas individuales.
  const standardEnabled =
    polygonTypeFilter === 'all' || polygonTypeFilter === 'standard';
  const blockingEnabled =
    polygonTypeFilter === 'all' || polygonTypeFilter === 'blocking';

  const standardQuery = useGetPolygonsByMedianZoneQuery(
    {
      country,
      median_zone: medianZoneId,
      polygon_type: 'standard',
      radius: getRadiusByCountry(country, 'standard'),
    },
    { skip: !medianZoneId || !country || !standardEnabled }
  );

  const blockingQuery = useGetPolygonsByMedianZoneQuery(
    {
      country,
      median_zone: medianZoneId,
      polygon_type: 'blocking',
      radius: getRadiusByCountry(country, 'blocking'),
    },
    { skip: !medianZoneId || !country || !blockingEnabled }
  );

  const polygonsData = useMemo(() => {
    if (polygonTypeFilter === 'standard') return standardQuery.data;
    if (polygonTypeFilter === 'blocking') return blockingQuery.data;
    if (standardQuery.data === undefined && blockingQuery.data === undefined) {
      return undefined;
    }
    return [...(standardQuery.data ?? []), ...(blockingQuery.data ?? [])];
  }, [polygonTypeFilter, standardQuery.data, blockingQuery.data]);

  useEffect(() => {
    if (data) {
      dispatch(updateIntersectionListHandlerFromServerData(data));
      dispatch(updateIntersectionHandlerListDirectly());
    }
  }, [data, dispatch]);

  useEffect(() => {
    if (polygonsData !== undefined) {
      dispatch(setListPolygons(polygonsData));
    }
  }, [polygonsData, dispatch]);

  const isFetchingPolygons =
    (standardEnabled && standardQuery.isFetching) ||
    (blockingEnabled && blockingQuery.isFetching);
  const isErrorPolygons =
    (standardEnabled && standardQuery.isError) ||
    (blockingEnabled && blockingQuery.isError);

  useEffect(() => {
    if (isFetchingPolygons) {
      toast.loading('Cargando polígonos...', { toastId: 'loadingListPolygons' });
    } else {
      toast.dismiss('loadingListPolygons');
    }
  }, [isFetchingPolygons]);

  useEffect(() => {
    if (isErrorPolygons) {
      toast.error('Error obteniendo polígonos');
    }
  }, [isErrorPolygons]);

  return (
    <Layout>
      <SidebarPolygons />
      <div className="h-100 w-100">
        <PolygonActionsBar />
        <Row
          className="m-0"
          style={{ height: 'calc(100% - 75px)', width: '100%' }}
        >
          <LayoutPolygonTool />
          <Col className="p-0">
            <GoogleMap
              containerStyle={{
                height: '100%',
                width: '100%',
                border: `3px solid ${borderColor}`,
              }}
              defaultZoom={17}
            >
              <PolygonsIntersection />
              <DrawingPolygon />
              <EditorMode />
              <PolygonsInMedianZones />
              <CenterMedianZone />
              <ListPolygons showPolygons={showPolygons} />
              <AuditSelectedPolygon />
            </GoogleMap>
          </Col>
        </Row>
      </div>
    </Layout>
  );
};
